'use client';

import * as React from 'react';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import { Toggle } from '@base-ui/react/toggle';
import clsx from 'clsx';
import styles from './SegmentedControl.module.css';

export interface SegmentedControlOption<V extends string = string> {
  value: V;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<V extends string = string> {
  options: SegmentedControlOption<V>[];
  value?: V;
  defaultValue?: V;
  onChange?: (value: V) => void;
  disabled?: boolean;
  className?: string;
}

export function SegmentedControl<V extends string = string>({
  options,
  value,
  defaultValue,
  onChange,
  disabled,
  className,
}: SegmentedControlProps<V>) {
  const controlled = value !== undefined ? [value] : undefined;
  const uncontrolled = defaultValue !== undefined ? [defaultValue] : undefined;

  return (
    <ToggleGroup
      className={clsx(styles.root, className)}
      value={controlled}
      defaultValue={uncontrolled}
      onValueChange={(vals) => {
        if (vals.length > 0 && onChange) onChange(vals[0] as V);
      }}
      disabled={disabled}
    >
      {options.map((opt) => (
        <Toggle
          key={opt.value}
          value={opt.value}
          disabled={opt.disabled}
          className={styles.option}
          aria-label={typeof opt.label === 'string' ? opt.label : undefined}
        >
          {opt.label}
        </Toggle>
      ))}
    </ToggleGroup>
  );
}
